/**
 * Group Management API Service
 * Handles all group-related API calls
 */

import { GroupType, IGroup } from '@hrms/shared/types';

import { apiClient } from './client';

export interface CreateGroupData {
  name: string;
  code?: string;
  description?: string;
  type: GroupType;
  role?: string;
  members?: string[];
  owner?: string;
  metadata?: Record<string, unknown>;
  isCoreTeam?: boolean;
}

export interface UpdateGroupData {
  name?: string;
  description?: string;
  owner?: string;
  metadata?: Record<string, unknown>;
  isActive?: boolean;
  isCoreTeam?: boolean;
}

export interface GroupFilters {
  type?: GroupType;
  role?: string;
  isActive?: boolean;
  owner?: string;
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface IGroupUpdate {
  _id: string;
  name: string;
  code: string;
  description?: string;
  type: GroupType;
  role?: string;
  members: string[]; // user ids
  membersCount: number;
  // The backend populates `owner` with the user document when possible, but
  // falls back to the raw (unpopulated) ObjectId string if population fails.
  owner:
    | string
    | {
        _id: string;
        name?: string;
        email?: string;
      };
  metadata?: Record<string, unknown>;
  isActive: boolean;
  isDefaultForRole?: boolean;
  isCoreTeam?: boolean;
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface GroupsListResponse {
  groups: IGroup[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface GroupMembersResponse {
  members: Array<{
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
    department?: string;
    designation?: string;
    avatar?: string;
    empId?: string;
  }>;
  totalCount: number;
  totalPages: number;
  currentPage: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface GroupMembershipRequest {
  userIds: string[];
}

export const groupsApi = {
  // Get groups list with filters
  getGroups: async (filters?: GroupFilters): Promise<GroupsListResponse> => {
    const response = await apiClient.get('/groups', { params: filters });
    return response.data.data;
  },

  // Get group by ID
  getGroupById: async (id: string): Promise<IGroupUpdate> => {
    const response = await apiClient.get(`/groups/${id}`);
    return response.data.data.group;
  },

  // Create new group.
  // Unwraps `data.group` to match updateGroup/getGroupById. The backend returns
  // `{ data: { group } }` for all three; this client previously returned the
  // envelope, which is why IGroup carried a phantom `group: any` field.
  createGroup: async (groupData: CreateGroupData): Promise<IGroup> => {
    const response = await apiClient.post('/groups', groupData);
    return response.data.data.group;
  },

  // Update group
  updateGroup: async (id: string, groupData: UpdateGroupData): Promise<IGroup> => {
    const response = await apiClient.put(`/groups/${id}`, groupData);
    return response.data.data.group;
  },

  // Delete group
  deleteGroup: async (id: string): Promise<void> => {
    await apiClient.delete(`/groups/${id}`);
  },

  // Search groups
  searchGroups: async (query: string): Promise<IGroup[]> => {
    const response = await apiClient.get('/groups/search', { params: { q: query } });
    return response.data.data;
  },

  // Get user's groups
  getMyGroups: async (): Promise<IGroup[]> => {
    const response = await apiClient.get('/groups/my-groups');
    return response.data.data;
  },

  // Get groups by role
  getGroupsByRole: async (role: string): Promise<IGroup[]> => {
    const response = await apiClient.get(`/groups/by-role/${role}`);
    return response.data.data;
  },

  // Get group members
  getGroupMembers: async (
    id: string,
    page?: number,
    limit?: number
  ): Promise<GroupMembersResponse> => {
    const response = await apiClient.get(`/groups/${id}/members`, {
      params: { page, limit },
    });
    return response.data.data;
  },

  // Add member to group
  addMember: async (id: string, userIds: string[]): Promise<void> => {
    await apiClient.post(`/groups/${id}/members`, { userIds });
  },

  // Remove member from group
  removeMember: async (id: string, userId: string): Promise<void> => {
    await apiClient.delete(`/groups/${id}/members/${userId}`);
  },

  // Sync role-based group
  syncRoleGroup: async (id: string): Promise<IGroupUpdate> => {
    const response = await apiClient.post(`/groups/${id}/sync`);
    return response.data.data;
  },

  // Sync all role groups
  syncAllRoleGroups: async (): Promise<void> => {
    await apiClient.post('/groups/sync-role-groups');
  },

  // Export group members
  exportGroupMembers: async (id: string): Promise<Blob> => {
    const response = await apiClient.get(`/groups/${id}/export`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // Add multiple members to group
  addMembers: async (id: string, membershipData: GroupMembershipRequest): Promise<void> => {
    await apiClient.post(`/groups/${id}/members`, membershipData);
  },

  // Remove multiple members from group
  removeMembers: async (id: string, userIds: string[]): Promise<void> => {
    await apiClient.delete(`/groups/${id}/members`, { data: { userIds } });
  },
};
